import { isNull } from 'lodash-es'
import { useTranslations } from 'next-intl'

import { Link } from '@/shared/i18n'
import { useFormatDate } from '@/shared/lib'
import { Button, Dialog } from '@/shared/ui'

import { useGuestDepositReturnCard } from '../../api/get-guest-deposit-return-card'

type Props = {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  contractId: number
}

export const UpdateDepositReturnCardDialog = ({
  isOpen,
  onOpenChange,
  contractId,
}: Props) => {
  const t = useTranslations('deposit_information')
  const { formatDateWithDayOfWeek } = useFormatDate()

  const { depositReturnCard } = useGuestDepositReturnCard({
    contractId,
  })

  const isDepositReturned =
    depositReturnCard?.isDepositReturned &&
    !isNull(depositReturnCard?.depositReturnedAt)

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content className="h-[362px] gap-0 p-0">
        <Dialog.Header className="p-6">
          <Dialog.Title className="text-start">{t('title')}</Dialog.Title>
        </Dialog.Header>
        <div className="grow px-6">
          <h3 className="typo-body1 mb-3 font-semibold text-black">
            {t('refund_card.title')}
          </h3>

          <div className="mb-4 space-y-2">
            <p className="typo-label1 font-medium text-gray-900">
              {depositReturnCard?.cardName} {depositReturnCard?.cardNo}
            </p>

            {isDepositReturned && (
              <p className="typo-body1 font-bold text-purple-500">
                {t('date_status.complete', {
                  date: formatDateWithDayOfWeek({
                    date: depositReturnCard.depositReturnedAt!,
                  }),
                })}
              </p>
            )}

            <p className="typo-label2 font-normal whitespace-break-spaces text-gray-500">
              {t('refund_card.description')}
            </p>
          </div>

          {isDepositReturned && (
            <Button
              variant="outline"
              size="medium"
              shape="pill"
              fullWidth
              asChild
            >
              <Link
                href={depositReturnCard.cardReceiptUrl ?? ''}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('button.card_receipt')}
              </Link>
            </Button>
          )}
        </div>
        <Dialog.Footer className="flex-row gap-2.5 p-6">
          <Dialog.Close asChild>
            <Button
              type="button"
              variant="solid"
              color="secondary"
              size="large"
              fullWidth
            >
              {t('button.close')}
            </Button>
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}
